/**
 * Module dependencies.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Stablecoins are pinned to 1 USD (no stream needed).
 */

const STABLES = new Set(['USDT', 'USDC', 'DAI', 'BUSD', 'USD']);

/**
 * `useLivePrices` — subscribe to live USD prices for a set of symbols via
 * Binance's public combined websocket (miniTicker). Returns a `{ SYMBOL: usd }`
 * map that updates in real time. Reconnects automatically; cleans up on unmount.
 *
 * Note: connects directly to Binance's public stream (leaks which assets are
 * watched, not ownership) — matching Crypto Gest's price stance.
 */

export function useLivePrices(symbols: string[]): Record<string, number> {
  const [prices, setPrices] = useState<Record<string, number>>({});

  // A stable dependency key so the socket only reconnects when the set changes.
  const key = Array.from(new Set(symbols.map(symbol => symbol.toUpperCase())))
    .filter(symbol => symbol && !STABLES.has(symbol))
    .sort()
    .join(',');

  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const list = key ? key.split(',') : [];

    if (list.length === 0) {
      return;
    }

    const streams = list.map(symbol => `${symbol.toLowerCase()}usdt@miniTicker`).join('/');
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout>;

    const connect = () => {
      const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
      wsRef.current = ws;

      ws.onmessage = event => {
        try {
          const message = JSON.parse(event.data);
          const data = message?.data;

          if (data?.s && data?.c) {
            const symbol = String(data.s).replace(/USDT$/, '');

            setPrices(prev => ({ ...prev, [symbol]: parseFloat(data.c) }));
          }
        } catch {
          // Ignore malformed frames.
        }
      };

      ws.onclose = () => {
        if (!closed) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [key]);

  return prices;
}
